import { Log } from "./log.js";
import { IdGenerator } from "./idgenerator.js";
import { RoundModel } from "./roundmodel.js";
import { Box2D } from "./box2d.js";
import { Constants } from "./constants.js";
import { Weapon } from "./weapon.js";
import { LaserWeapon } from "./laserweapon.js";
import { DoubleBarrelWeapon } from "./doublebarrelweapon.js";
import { ShotgunWeapon } from "./shotgunweapon.js";
import { HomingMissileWeapon } from "./homingmissileweapon.js";
import { MineWeapon } from "./mineweapon.js";
import { GatlingGunWeapon } from "./gatlinggunweapon.js";
import { Upgrade } from "./upgrade.js";
import { LaserAimerUpgrade } from "./laseraimerupgrade.js";
import { AimerUpgrade } from "./aimerupgrade.js";
import { ShieldUpgrade } from "./shieldupgrade.js";
import { SpeedBoostUpgrade } from "./speedboostupgrade.js";
import { Zone } from "./zone.js";
import { Kill } from "./kill.js";
import { Trip } from "./trip.js";
import { Pickup } from "./pickup.js";
import { WeaponDeactivation } from "./weapondeactivation.js";
import { UpgradeUpdate } from "./upgradeupdate.js";
import { VictoryAward } from "./victoryaward.js";
import { TankState } from "./tankstate.js";
import { CollectibleState } from "./collectiblestate.js";

export class RoundController {
    model = null;
    localPlayerIds = [];
    gameMode = null;
    id = null;
    initialRoundStateReceived = false;
    initialRoundStateSent = false;
    tankStateEmissionValue = 0.0;
    log = null;

    constructor(localPlayerIds, gameMode, gameId) {
        this.id = IdGenerator.generate("rc");
        this.localPlayerIds = localPlayerIds;
        this.gameMode = gameMode;
        this.log = new Log("RoundController");
        this.model = new RoundModel(this.id, new Box2D.Dynamics.b2World(new Box2D.Common.Math.b2Vec2(0, 0), true));
        // We only want to handle events generated by the model when we are not
        // receiving instructions from the outside (ie. only if we are in local mode,
        // or in server mode)
        // We also only want to register the round controller on the game mode in this case.
        if (Constants.getMode() === Constants.MODE_SERVER || Constants.getMode() === Constants.MODE_CLIENT_LOCAL) {
            this.model.addEventListener(this._modelEventHandler, this, gameId);

            // Store reference to this new round controller.
            this.gameMode.setRoundController(this);
        }
    }

    _modelEventHandler(self, id, evt, data) {
        switch (evt) {
            case RoundModel._EVENTS.ROUND_CREATED:
            case RoundModel._EVENTS.MAZE_SET:
            case RoundModel._EVENTS.ROUND_STARTED:
            case RoundModel._EVENTS.ROUND_ENDED:
            case RoundModel._EVENTS.CELEBRATION_STARTED:
            case RoundModel._EVENTS.CELEBRATION_ENDED:
            case RoundModel._EVENTS.PROJECTILE_TIMEOUT:
            case RoundModel._EVENTS.PROJECTILE_CREATED:
            case RoundModel._EVENTS.PROJECTILE_DESTROYED:
            case RoundModel._EVENTS.TRAP_CREATED:
            case RoundModel._EVENTS.TRAP_TRIPPED:
            case RoundModel._EVENTS.TRAP_DESTROYED:
            case RoundModel._EVENTS.COLLECTIBLE_CREATED:
            case RoundModel._EVENTS.COLLECTIBLE_DESTROYED:
            case RoundModel._EVENTS.TANK_CREATED:
            case RoundModel._EVENTS.TANK_DESTROYED:
            case RoundModel._EVENTS.TANK_KILLED:
            case RoundModel._EVENTS.WEAPON_CREATED:
            case RoundModel._EVENTS.WEAPON_DESTROYED:
            case RoundModel._EVENTS.UPGRADE_CREATED:
            case RoundModel._EVENTS.UPGRADE_DESTROYED:
            case RoundModel._EVENTS.COUNTER_CREATED:
            case RoundModel._EVENTS.COUNTER_DESTROYED:
            case RoundModel._EVENTS.ZONE_CREATED:
            case RoundModel._EVENTS.ZONE_DESTROYED:
            case Weapon._EVENTS.WEAPON_FIRED:
            case Weapon._EVENTS.WEAPON_EMPTY:
            case Weapon._EVENTS.HOMING_MISSILE_TARGET_CHANGED:
            case Weapon._EVENTS.MINE_ACTIVATED:
            case Weapon._EVENTS.MINE_TRIPPED:
            case Weapon._EVENTS.MINE_DETONATED:
            case Upgrade._EVENTS.UPGRADE_ACTIVATED:
            case Upgrade._EVENTS.UPGRADE_WEAKENED:
            case Upgrade._EVENTS.UPGRADE_STRENGTHENED:
            case Zone._EVENTS.ZONE_ENTERED:
            case Zone._EVENTS.ZONE_LEFT:
            case Zone._EVENTS.ZONE_DESTABILISED:
            case RoundModel._EVENTS.TANK_TANK_COLLISION:
            case RoundModel._EVENTS.TANK_MAZE_COLLISION:
            case RoundModel._EVENTS.TANK_SHIELD_COLLISION:
            case RoundModel._EVENTS.SHIELD_SHIELD_COLLISION:
            {
                // Intentionally left blank
                break;
            }
            case Weapon._EVENTS.WEAPON_DELAYED_FIRE:
            {
                self.delayedFire(data);
                break;
            }
            case RoundModel._EVENTS.SHIELD_ZONE_COLLISION:
            {
                switch (data.zone.getType()) {
                    case Constants.ZONE_TYPES.SPAWN:
                    {
                        const upgradeUpdate = new UpgradeUpdate(data.shieldA.getId(), data.shieldA.getPlayerId());
                        self.destroyUpgrade(upgradeUpdate);
                        break;
                    }
                    case Constants.ZONE_TYPES.STORM:
                    {
                        const upgradeUpdate = new UpgradeUpdate(data.shieldA.getId(), data.shieldA.getPlayerId());
                        self.destroyUpgrade(upgradeUpdate);
                        break;
                    }
                }
                break;
            }
            case RoundModel._EVENTS.PROJECTILE_SHIELD_COLLISION:
            {
                data.projectile.hitShield();
                break;
            }
            case RoundModel._EVENTS.PROJECTILE_ZONE_COLLISION:
            {
                switch (data.zone.getType()) {
                    case Constants.ZONE_TYPES.SPAWN:
                    {
                        self.destroyProjectile(data.projectile.getId());
                        break;
                    }
                }
                break;
            }
            case RoundModel._EVENTS.PROJECTILE_MAZE_COLLISION:
            {
                data.projectile.hitMaze();
                break;
            }
            case RoundModel._EVENTS.TANK_COLLECTIBLE_COLLISION:
            {
                const pickup = new Pickup(data.tankA.getPlayerId(), data.collectible.getId(), data.collectible.getType());

                // Check that tank has room to pick up crate.
                if (data.collectible.getType() < Constants.COLLECTIBLE_TYPES.WEAPON_CRATE_COUNT) {
                    if (self.getQueuedWeapons(data.tankA.getPlayerId()).length < Constants.MAX_WEAPON_QUEUE) {
                        self.pickUpCrate(pickup);
                        self.destroyCollectible(pickup);
                    }
                } else if (data.collectible.getType() < Constants.COLLECTIBLE_TYPES.UPGRADE_CRATE_COUNT) {
                    if (!self.getUpgradeByPlayerIdAndType(data.tankA.getPlayerId(), data.collectible.getType() + Constants.COLLECTIBLE_TYPES.COLLECTIBLE_TO_UPGRADE_OFFSET)) {
                        self.pickUpCrate(pickup);
                        self.destroyCollectible(pickup);
                    }
                } else {
                    self.destroyCollectible(pickup);
                }
                break;
            }
            case RoundModel._EVENTS.TANK_DEADLY_COLLISION:
            {
                let killExperience = 0;
                if (data.tankA.getPlayerId() !== data.projectile.getPlayerId()) {
                    killExperience = self.gameMode.getKillExperience();
                }

                const kill = new Kill(data.tankA.getPlayerId(), data.projectile.getPlayerId(), killExperience, data.projectile.getId(), data.projectile.getType());
                self.killTank(kill);
                self.destroyProjectile(data.projectile.getId());
                break;
            }
            case RoundModel._EVENTS.TANK_TRAP_COLLISION:
            {
                const trip = new Trip(data.tankA.getPlayerId(), data.trap.getId(), data.trap.getType(), true);
                self.tripTrap(trip);
                break;
            }
            case RoundModel._EVENTS.TANK_TRAP_END_COLLISION:
            {
                const trip = new Trip(data.tankA.getPlayerId(), data.trap.getId(), data.trap.getType(), false);
                self.tripTrap(trip);
                break;
            }
            case RoundModel._EVENTS.TANK_ZONE_COLLISION:
            {
                switch (data.zone.getType()) {
                    case Constants.ZONE_TYPES.SPAWN:
                    {
                        self.destroyTank(data.tankA.getPlayerId());
                        break;
                    }
                    case Constants.ZONE_TYPES.STORM:
                    {
                        self.destroyTank(data.tankA.getPlayerId());
                        break;
                    }
                }
                break;
            }
            case RoundModel._EVENTS.TRAP_ZONE_COLLISION:
            {
                switch (data.zone.getType()) {
                    case Constants.ZONE_TYPES.SPAWN:
                    {
                        self.destroyTrap(data.trap.getId());
                        break;
                    }
                }
                break;
            }
            case RoundModel._EVENTS.TANK_CHICKENED_OUT:
            {
                // A player disconnected while he was alive in a round that was in progress
                // Intentionally do nothing here
                break;
            }
            default:
            {
                self.log.error(`Unknown event received by RoundController._modelEventHandler: ${evt}`);
            }
        }
    }

    removeTank(playerId) {
        return this.model.removeTank(playerId);
    }

    setMaze(maze) {
        this.model.setMaze(maze);
    }

    setRoundState(roundState) {
        //Iterate over tanks, projectiles, etc., and call setTankState, and setProjectileState etc.
        const tankStates = roundState.getTankStates();
        for (const tankState of tankStates) {
            // If online client and the player id is my player id, ignore the tank state.
            // Except if the tank is not present (it has spawned). Since, we use websockets we are guaranteed that a round state containing a previously destroyed tank will never show up after the tank destruction message.
            if (Constants.getMode() !== Constants.MODE_CLIENT_ONLINE || !this.localPlayerIds.includes(tankState.getPlayerId()) || this.model.getTanks()[tankState.getPlayerId()] === undefined) {
                this.setTankState(tankState, false);
            }
        }

        const projectileStates = roundState.getProjectileStates();
        for (const projectileState of projectileStates) {
            this.setProjectileState(projectileState);
        }

        const trapStates = roundState.getTrapStates();
        for (const trapState of trapStates) {
            this.setTrapState(trapState);
        }

        const collectibleStates = roundState.getCollectibleStates();
        for (const collectibleState of collectibleStates) {
            this.setCollectibleState(collectibleState);
        }

        const weaponStates = roundState.getWeaponStates();
        for (const weaponState of weaponStates) {
            this.setWeaponState(weaponState);
        }

        const upgradeStates = roundState.getUpgradeStates();
        for (const upgradeState of upgradeStates) {
            this.setUpgradeState(upgradeState);
        }

        const counterStates = roundState.getCounterStates();
        for (const counterState of counterStates) {
            this.setCounterState(counterState);
        }

        const zoneStates = roundState.getZoneStates();
        for (const zoneState of zoneStates) {
            this.setZoneState(zoneState);
        }

        this.initialRoundStateReceived = true;
    }

    getRoundState(expandedState) {
        expandedState |= !this.initialRoundStateSent;
        const roundState = this.model.getRoundState(expandedState);

        return roundState;
    }

    clearExpandedRoundStateBits() {
        this.initialRoundStateSent = true;
        this.model.clearExpandedRoundStateBits();
    }

    verifyAndCorrectTankState(tankState) {
        // Do some verification of the tank state - only used in server mode.
        const tank = this.getTank(tankState.getPlayerId());

        if (!tank) {
            return true;
        }

        // Keeps tabs of whether or not the state was modified because it was invalid.
        // If it is modified, signal that the client's state should be overwritten by the server's modified state.
        let stateVerified = true;

        if (isNaN(tankState.getX()) || isNaN(tankState.getY())) {
            this.log.error("Received NaN tank position in tank state");
            // Client's position is not valid.
            tankState.setX(tank.getX());
            tankState.setY(tank.getY());
            stateVerified = false;
        }

        if (isNaN(tankState.getRotation())) {
            this.log.error("Received NaN tank rotation in tank state");
            // Client's rotation is not valid
            tankState.setRotation(tank.getRotation());
            stateVerified = false;
        }

        const positionDiff = Box2D.Common.Math.b2Vec2.Make(tank.getX() - tankState.getX(), tank.getY() - tankState.getY());
        if (positionDiff.LengthSquared() > Constants.SERVER.MAX_ACCEPTED_POSITION_DIFF_SQUARED) {
            // Client's position needs to be corrected.
            // FIXME Perhaps set the position to extrapolated version based on client's latency.
            tankState.setX(tank.getX());
            tankState.setY(tank.getY());
            stateVerified = false;
        }
        const rotationDiff = tank.getRotation() - tankState.getRotation();
        if (Math.abs(rotationDiff) > Constants.SERVER.MAX_ACCEPTED_ROTATION_DIFF) {
            // Client's rotation needs to be corrected.
            // FIXME Perhaps set the rotation to extrapolated version based on client's latency.
            tankState.setRotation(tank.getRotation());
            stateVerified = false;
        }

        const maze = this.getMaze();
        if (maze) {
            if (!maze.isTankStateInsideMaze(tankState)) {
                // Client's position needs to be corrected.
                tankState.setX(tank.getX());
                tankState.setY(tank.getY());
                stateVerified = false;
            }
        }
        if (maze) {
            const tankPosition = { x: Math.floor(tank.getX() / Constants.MAZE_TILE_SIZE.m), y: Math.floor(tank.getY() / Constants.MAZE_TILE_SIZE.m) };
            const tankStatePosition = { x: Math.floor(tankState.getX() / Constants.MAZE_TILE_SIZE.m), y: Math.floor(tankState.getY() / Constants.MAZE_TILE_SIZE.m) };
            const mazeDistance = maze.getDistanceBetweenPositions(tankPosition, tankStatePosition);
            if (mazeDistance > Constants.SERVER.MAX_ACCEPTED_MAZE_DISTANCE) {
                // Client's position needs to be corrected.
                tankState.setX(tank.getX());
                tankState.setY(tank.getY());
                stateVerified = false;
            }
        }

        /*if (!B2DUtils.verifyTankStatePhysics(this.getB2DWorld(), tank.getB2DBody(), tankState)) {
            // Client's position and rotation needs to be corrected.
            tankState.setX(tank.getX());
            tankState.setY(tank.getY());
            tankState.setRotation(tank.getRotation());
            stateVerified = false;
        }*/

        return stateVerified;
    }

    setTankState(tankState, initial) {
        // Only do game logic if local client or server.
        if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL || Constants.getMode() === Constants.MODE_SERVER) {
            // Check that tank is still alive.
            const tank = this.getTank(tankState.getPlayerId());
            const tankDestroyed = this.model.isTankDestroyed(tankState.getPlayerId());
            if ((!tank || tankDestroyed) && !initial) {
                return false;
            }

            this.model.setTankState(tankState);
        } else {
            this.model.setTankState(tankState);
        }

        // Update weapons on both client and server side.
        this._updateWeaponLockingAndFiring();

        return true;
    }

    setProjectileState(projectileState) {
        this.model.setProjectileState(projectileState);
    }

    setTrapState(trapState) {
        this.model.setTrapState(trapState);
    }

    setCollectibleState(collectibleState) {
        this.model.setCollectibleState(collectibleState);
    }

    setWeaponState(weaponState) {
        this.model.setWeaponState(weaponState);
    }

    setUpgradeState(upgradeState) {
        this.model.setUpgradeState(upgradeState);
    }

    setCounterState(counterState) {
        this.model.setCounterState(counterState);
    }

    setZoneState(zoneState) {
        this.model.setZoneState(zoneState);
    }

    setInputState(inputState) {
        // Disable input if round is not started or maze was not yet received.
        if (!this.model.getStarted() || !this.model.getMaze()) {
            return;
        }

        const playerId = inputState.getPlayerId();
        const tank = this.getTank(playerId);
        if (tank) {
            const tankState = TankState.withState(
                playerId,
                tank.getX(),
                tank.getY(),
                inputState.getForward(),
                inputState.getBack(),
                tank.getRotation(),
                inputState.getLeft(),
                inputState.getRight(),
                inputState.getFire(),
                tank.getLocked()
            );

            this.setTankState(tankState, false);

            if (Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
                this.tankStateEmissionValue = 0.0;
                this.model.emitTankState(tankState);
            }
        }
    }

    createRound(ranked) {
        this.model.createRound(ranked);
    }

    startRound() {
        // If local client, we never receive an initial round state so set this to true to make UI work.
        if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL) {
            this.initialRoundStateReceived = true;
        }

        this.model.startRound();
    }

    endRound(victoryAward) {
        this.model.endRound(victoryAward);
        // Reset state.
        this.initialRoundStateReceived = false;
    }

    startCelebration() {
        this.model.startCelebration();
    }

    endCelebration() {
        this.model.endCelebration();
    }

    pullTrigger(playerId) {
        const tank = this.getTank(playerId);
        if (tank) {
            const weapon = this.getActiveWeapon(playerId);
            if (weapon) {
                // Fire correct weapon according to tank's current weapon.
                if (weapon.fire()) {
                    // Only do game logic if local client or server.
                    if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL || Constants.getMode() === Constants.MODE_SERVER) {
                        // Add the weapon's projectiles.
                        const projectileStates = weapon.getProjectileStates(tank);
                        for (const projectileState of projectileStates) {
                            this.setProjectileState(projectileState);
                        }
                        // Add the weapon's traps.
                        const trapStates = weapon.getTrapStates(tank);
                        for (const trapState of trapStates) {
                            this.setTrapState(trapState);
                        }
                    }
                }
            }
        }
    }

    releaseTrigger(playerId) {
        const tank = this.getTank(playerId);
        if (tank) {
            const weapon = this.getActiveWeapon(playerId);
            if (weapon) {
                weapon.release();
            }
        }
    }

    delayedFire(playerId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.delayedFire(playerId);
        } else {
            this.log.error("Attempt to fire projectile delayed while round was not started");
        }
    }

    timeoutProjectile(projectileId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.timeoutProjectile(projectileId);
        } else {
            this.log.error("Attempt to timeout projectile while round was not started");
        }
    }

    destroyProjectile(projectileId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyProjectile(projectileId);
        } else {
            this.log.error("Attempt to destroy projectile while round was not started");
        }
    }

    tripTrap(trip) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.tripTrap(trip);
        } else {
            this.log.error("Attempt to trip trap while round was not started");
        }
    }

    destroyTrap(trapId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyTrap(trapId);
        } else {
            this.log.error("Attempt to destroy trap while round was not started");
        }
    }

    destroyCollectible(pickup) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyCollectible(pickup);
        } else {
            this.log.error("Attempt to destroy collectible while round was not started");
        }
    }

    destroyWeapon(weaponDeactivation) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyWeapon(weaponDeactivation);
        } else {
            this.log.error("Attempt to destroy weapon while round was not started");
        }
    }

    destroyUpgrade(upgradeUpdate) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyUpgrade(upgradeUpdate);
        } else {
            this.log.error("Attempt to destroy upgrade while round was not started");
        }
    }

    destroyCounter(counterId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyCounter(counterId);
        } else {
            this.log.error("Attempt to destroy counter while round was not started");
        }
    }

    destroyZone(zoneId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyZone(zoneId);
        } else {
            this.log.error("Attempt to destroy zone while round was not started");
        }
    }

    killTank(kill) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.killTank(kill);
        } else {
            this.log.error("Attempt to kill tank while round was not started");
        }
    }

    destroyTank(playerId) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.model.destroyTank(playerId);
        } else {
            this.log.error("Attempt to destroy tank while round was not started");
        }
    }

    spawnTank(playerId, position, respawn) {
        const tankState = TankState.withState(
            playerId,
            position.x,
            position.y,
            false,
            false,
            position.rotation,
            false,
            false,
            false,
            false
        );

        this.setTankState(tankState, true);

        let weaponState = null;
        let upgradeState = null;
        if (respawn) {
            weaponState = this.gameMode.getRespawnWeaponState(tankState.getPlayerId());
            upgradeState = this.gameMode.getRespawnUpgradeState(tankState.getPlayerId());
        } else {
            weaponState = this.gameMode.getInitialWeaponState(tankState.getPlayerId());
            upgradeState = this.gameMode.getInitialUpgradeState(tankState.getPlayerId());
        }
        if (weaponState) {
            this.setWeaponState(weaponState);
        }
        if (upgradeState) {
            this.setUpgradeState(upgradeState);
        }
    }

    spawnCrate(type, position) {
        const collectibleState = CollectibleState.withState(
            IdGenerator.generate("c"),
            type,
            position.x,
            position.y,
            position.rotation
        );

        this.setCollectibleState(collectibleState);
    }

    pickUpCrate(pickup) {
        if (this.model.getStarted() || Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            let weaponState = null;
            let upgradeState = null;

            const collectible = this.getCollectible(pickup.getCollectibleId());
            if (collectible) {
                switch (collectible.getType()) {
                    case Constants.COLLECTIBLE_TYPES.CRATE_LASER:
                    {
                        weaponState = LaserWeapon.createInitialWeaponState(
                            IdGenerator.generate("lw"),
                            pickup.getPlayerId());

                        upgradeState = LaserAimerUpgrade.createInitialUpgradeState(
                            IdGenerator.generate("lau"),
                            pickup.getPlayerId(),
                            weaponState.getId(),
                            Constants.LASER_AIMER_LENGTH);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_DOUBLE_BARREL:
                    {
                        weaponState = DoubleBarrelWeapon.createInitialWeaponState(
                            IdGenerator.generate("dbw"),
                            pickup.getPlayerId(),
                            Constants.DOUBLE_BARREL_AMMO_COUNT);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_SHOTGUN:
                    {
                        weaponState = ShotgunWeapon.createInitialWeaponState(
                            IdGenerator.generate("sw"),
                            pickup.getPlayerId(),
                            Constants.SHOTGUN_AMMO_COUNT);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_HOMING_MISSILE:
                    {
                        weaponState = HomingMissileWeapon.createInitialWeaponState(
                            IdGenerator.generate("hmw"),
                            pickup.getPlayerId());

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_MINE:
                    {
                        weaponState = MineWeapon.createInitialWeaponState(
                            IdGenerator.generate("mw"),
                            pickup.getPlayerId(),
                            Constants.MINE_AMMO_COUNT);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_GATLING_GUN:
                    {
                        weaponState = GatlingGunWeapon.createInitialWeaponState(
                            IdGenerator.generate("ggw"),
                            pickup.getPlayerId(),
                            Constants.GATLING_GUN_AMMO_COUNT);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_AIMER:
                    {
                        upgradeState = AimerUpgrade.createInitialUpgradeState(
                            IdGenerator.generate("au"),
                            pickup.getPlayerId(),
                            Constants.AIMER_LIFETIME,
                            Constants.AIMER_LENGTH);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_SHIELD:
                    {
                        upgradeState = ShieldUpgrade.createInitialUpgradeState(
                            IdGenerator.generate("su"),
                            pickup.getPlayerId(),
                            Constants.SHIELD_LIFETIME);

                        break;
                    }
                    case Constants.COLLECTIBLE_TYPES.CRATE_SPEED_BOOST:
                    {
                        upgradeState = SpeedBoostUpgrade.createInitialUpgradeState(
                            IdGenerator.generate("sbu"),
                            pickup.getPlayerId(),
                            Constants.SPEED_BOOST_LIFETIME,
                            Constants.SPEED_BOOST_EFFECT);

                        break;
                    }
                }

                if (weaponState) {
                    this.setWeaponState(weaponState);
                }

                if (upgradeState) {
                    this.setUpgradeState(upgradeState);
                }
            } else {
                this.log.error("Attempt to pick up crate which was not there");
            }
        } else {
            this.log.error("Attempt to pick up crate while round was not started");
        }
    }

    spawnGold() {
        // Check if max number of gold spawns for this round has been reached.
        if (this.model.getGoldSpawnCount() < Constants.SERVER.GOLD_SPAWN_MAX_PER_ROUND) {
            // Check if there is more than one tank left in the round.
            if (this.model.getTankCount() > 1) {
                // Check if there are already max number of simultaneous golds in the round.
                if (this.getCollectibleCount(Constants.COLLECTIBLE_TYPES.GOLD) < Constants.MAX_GOLDS) {
                    const goldPosition = this.model.getMaze().getRandomUnusedPosition(this.getRoundState(true), Constants.GOLD_MINIMUM_TILES_TO_TANKS);
                    if (goldPosition) {
                        const collectibleState = CollectibleState.withState(
                            IdGenerator.generate("g"),
                            Constants.COLLECTIBLE_TYPES.GOLD,
                            goldPosition.x,
                            goldPosition.y,
                            0
                        );

                        this.setCollectibleState(collectibleState);
                    }
                }
            }
        }
    }

    spawnDiamond() {
        // Check if max number of diamond spawns for this round has been reached.
        if (this.model.getDiamondSpawnCount() < Constants.SERVER.DIAMOND_SPAWN_MAX_PER_ROUND) {
            // Check if there is more than one tank left in the round.
            if (this.model.getTankCount() > 1) {
                // Check if there are already max number of simultaneous diamonds in the round.
                if (this.getCollectibleCount(Constants.COLLECTIBLE_TYPES.DIAMOND) < Constants.MAX_DIAMONDS) {
                    const diamondPosition = this.model.getMaze().getRandomUnusedPosition(this.getRoundState(true), Constants.DIAMOND_MINIMUM_TILES_TO_TANKS);
                    if (diamondPosition) {
                        const collectibleState = CollectibleState.withState(
                            IdGenerator.generate("d"),
                            Constants.COLLECTIBLE_TYPES.DIAMOND,
                            diamondPosition.x,
                            diamondPosition.y,
                            (-Math.PI * 0.25) + (Math.random() * 0.5 * Math.PI)
                        );

                        this.setCollectibleState(collectibleState);
                    }
                }
            }
        }
    }

    _updateWeaponLockingAndFiring() {
        // Lock movement on both client and server side.
        const tanks = this.model.getTanks();
        for (const tank in tanks) {
            const weapon = this.getActiveWeapon(tanks[tank].getPlayerId());
            if (weapon) {
                tanks[tank].setLocked(weapon.movementLocked());
            }
        }

        // Pull and release trigger on both client and server side.
        for (const tank in tanks) {
            if (tanks[tank].getFireDown()) {
                this.pullTrigger(tanks[tank].getPlayerId());
            } else {
                this.releaseTrigger(tanks[tank].getPlayerId());
            }
        }
    }

    update(deltaTime) {
        // Update weapons on both client and server side.
        this._updateWeaponLockingAndFiring();

        // Only do game logic if local client or server.
        if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL || Constants.getMode() === Constants.MODE_SERVER) {
            // Enable logic only if round is started and maze was created.
            if (this.model.getStarted() && this.model.getMaze()) {
                // Time out projectiles.
                const projectiles = this.model.getProjectiles();
                for (const projectile in projectiles) {
                    if (projectiles[projectile].done()) {
                        this.model.timeoutProjectile(projectiles[projectile].getId());
                    }
                }

                // Check whether traps are released.
                const traps = this.model.getTraps();
                for (const trap in traps) {
                    if (traps[trap].released()) {
                        // Add the trap's projectiles.
                        const projectileStates = traps[trap].getProjectileStates();
                        for (const projectileState of projectileStates) {
                            this.setProjectileState(projectileState);
                        }
                    }
                }

                // Check whether traps are done.
                for (const trap in traps) {
                    if (traps[trap].done()) {
                        this.destroyTrap(traps[trap].getId());
                    }
                }

                // Check whether weapons are done
                const weapons = this.model.getWeapons();
                for (const weapon in weapons) {
                    if (weapons[weapon].done()) {
                        const weaponDeactivation = new WeaponDeactivation(weapons[weapon].getId(), weapons[weapon].getPlayerId());
                        this.destroyWeapon(weaponDeactivation);
                    }
                }

                // Check whether upgrades are done
                const upgrades = this.model.getUpgrades();
                for (const upgrade in upgrades) {
                    if (upgrades[upgrade].done()) {
                        const upgradeUpdate = new UpgradeUpdate(upgrades[upgrade].getId(), upgrades[upgrade].getPlayerId());
                        this.destroyUpgrade(upgradeUpdate);
                    }
                }

                // Check whether counters are done
                const counters = this.model.getCounters();
                for (const counter in counters) {
                    if (counters[counter].done()) {
                        this.destroyCounter(counters[counter].getId());
                    }
                }

                // Check whether zones are done
                const zones = this.model.getZones();
                for (const zone in zones) {
                    if (zones[zone].done()) {
                        this.destroyZone(zones[zone].getId());
                    }
                }

                // Update game mode logic.
                this.gameMode.update(deltaTime);

                // Check whether round has ended
                if (this.gameMode.isRoundOver()) {
                    // Figure out if anyone won.
                    const winnerPlayerIds = this.gameMode.getWinnerPlayerIds();

                    let victoryExperiencePerWinner = 0;
                    if (winnerPlayerIds.length > 0) {
                        victoryExperiencePerWinner = Math.ceil(this.gameMode.getVictoryExperience() / winnerPlayerIds.length);
                    }

                    let victoryGoldAmountPerWinner = 0;
                    if (winnerPlayerIds.length > 0) {
                        victoryGoldAmountPerWinner = Math.ceil(this.model.getVictoryGoldAmount() / winnerPlayerIds.length);
                    }

                    const rankChanges = this.model.getRankChanges(winnerPlayerIds);

                    this.endRound(new VictoryAward(winnerPlayerIds, victoryExperiencePerWinner, victoryGoldAmountPerWinner, rankChanges));
                }
            }
        } else if (this.model.getStarted()) {
            // Only emit my tank state if online client.
            this.tankStateEmissionValue += deltaTime;
            if (this.tankStateEmissionValue >= Constants.CLIENT.TANKSTATE_EMISSION_INTERVAL) {
                this.tankStateEmissionValue = 0.0;
                for (const localPlayerId of this.localPlayerIds) {
                    const tank = this.getTank(localPlayerId);
                    if (tank) {
                        this.model.emitTankState(tank.getTankState());
                    }
                }
            }
        }

        // Update model.
        this.model.update(deltaTime);
    }

    addEventListener(callback, context, gameId) {
        this.model.addEventListener(callback, context, gameId);
    }

    removeEventListener(callback, context) {
        this.model.removeEventListener(callback, context);
    }

    getId() {
        return this.id;
    }

    getTank(playerId) {
        return this.model.getTank(playerId);
    }

    getTanks() {
        return this.model.getTanks();
    }

    getProjectile(projectileId) {
        return this.model.getProjectile(projectileId);
    }

    getProjectiles() {
        return this.model.getProjectiles();
    }

    getTrap(trapId) {
        return this.model.getTrap(trapId);
    }

    getTraps() {
        return this.model.getTraps();
    }

    getCollectible(collectibleId) {
        return this.model.getCollectible(collectibleId);
    }

    getCollectibles() {
        return this.model.getCollectibles();
    }

    getCrateCount() {
        return this.model.getCrateCount();
    }

    getCollectibleCount(collectibleType) {
        return this.model.getCollectibleCount(collectibleType);
    }

    getActiveWeapon(playerId) {
        return this.model.getActiveWeapon(playerId);
    }

    getDefaultWeapon(playerId) {
        return this.model.getDefaultWeapon(playerId);
    }

    getQueuedWeapons(playerId) {
        return this.model.getQueuedWeapons(playerId);
    }

    getUpgrades() {
        return this.model.getUpgrades();
    }

    getUpgrade(upgradeId) {
        return this.model.getUpgrade(upgradeId);
    }

    getUpgradeByPlayerIdAndType(playerId, upgradeType) {
        return this.model.getUpgradeByPlayerIdAndType(playerId, upgradeType);
    }

    getCounters() {
        return this.model.getCounters();
    }

    getCounter(counterId) {
        return this.model.getCounter(counterId);
    }

    getZones() {
        return this.model.getZones();
    }

    getZone(zoneId) {
        return this.model.getZone(zoneId);
    }

    getMaze() {
        return this.model.getMaze();
    }

    getModifier(playerId, modifierType) {
        return this.model.getModifier(playerId, modifierType);
    }

    setVictoryGoldAmount(victoryGoldAmount) {
        this.model.setVictoryGoldAmount(victoryGoldAmount);
    }

    setStakes(stakes) {
        this.model.setStakes(stakes);
    }

    getStake(playerId) {
        return this.model.getStake(playerId);
    }

    getInitialRoundStateReceived() {
        return this.initialRoundStateReceived;
    }

    getB2DWorld() {
        return this.model.getB2DWorld();
    }
}
